import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
import { Alert } from '../../components/ui/Alert';
import { AvatarUpload } from '../../components/ui/AvatarUpload';

export const Profile = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    job_title: '',
    company_name: '',
    bio: '',
    location: '',
    phone: '',
    website: '',
    avatar_url: '',
    networking_objective: ''
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        job_title: profile.job_title || '',
        company_name: profile.company_name || '',
        bio: profile.bio || '',
        location: profile.location || '',
        phone: profile.phone || '',
        website: profile.website || '',
        avatar_url: profile.avatar_url || '',
        networking_objective: profile.networking_objective || ''
      });
    }
  }, [profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ ...formData, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (error) throw error;
      
      await refreshProfile();
      setMessage({ type: 'success', text: 'Profile updated successfully.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update profile.' });
    } finally {
      setLoading(false);
    }
  };

  const objectives = [
    "Grow my professional network",
    "Find clients",
    "Generate sales",
    "Build partnerships",
    "Find speaking opportunities",
    "Find work",
    "Recruit talent",
    "Raise investment",
    "Build a community",
    "Mentor and learn",
    "Something else"
  ];

  return (
    <div className="max-w-4xl pb-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Your Profile</h1>
        <p className="mt-2 text-slate-600">Manage your central professional information.</p>
      </header>

      {message && (
        <Alert variant={message.type} message={message.text} className="mb-6 sticky top-4 z-10" />
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Information Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 md:p-8 space-y-8">
            <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-4">
              Personal Information
            </h2>
            
            <AvatarUpload 
              url={formData.avatar_url} 
              onUpload={(url) => setFormData({ ...formData, avatar_url: url })} 
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <Input 
                label={<>Full Name <span className="text-red-500">*</span></>} 
                value={formData.full_name} 
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
                required 
              />
              <div className="w-full">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email Address
                </label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-600 sm:text-sm">
                  {user?.email}
                </div>
              </div>
              <Input 
                label="Phone Number" 
                type="tel" 
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                placeholder="+1 (555) 000-0000"
              />
              <Input 
                label="Location" 
                placeholder="e.g. San Francisco, CA"
                value={formData.location} 
                onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
              />
            </div>
          </div>
        </div>

        {/* Professional Information Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 md:p-8 space-y-6">
            <h2 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-4">
              Professional Details & Goals
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input 
                label={<>Job Title or Role <span className="text-red-500">*</span></>} 
                value={formData.job_title} 
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} 
                required 
              />
              <Input 
                label="Company Name" 
                value={formData.company_name} 
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} 
              />
              <Input 
                label="Website" 
                type="url" 
                value={formData.website} 
                onChange={(e) => setFormData({ ...formData, website: e.target.value })} 
                placeholder="https://example.com"
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  What are you networking for?
                </label>
                <select 
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 sm:text-sm"
                  value={formData.networking_objective}
                  onChange={(e) => setFormData({ ...formData, networking_objective: e.target.value })}
                >
                  <option value="">Select your primary goal...</option>
                  {objectives.map(obj => (
                    <option key={obj} value={obj}>{obj}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Helps tailor your networking reviews.</p>
              </div>
            </div>
            
            <TextArea 
              label="Professional Bio" 
              value={formData.bio} 
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })} 
              placeholder="A brief summary of your professional background..."
              maxLength={500}
            />
          </div>
          <div className="bg-slate-50 px-6 py-4 md:px-8 flex justify-end border-t border-slate-200">
            <Button type="submit" isLoading={loading}>
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};